import { Button, Drawer, Radio, Tabs } from "antd"
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { FC, ReactNode, useState } from "react"
import { ITimeSheetDocsEntity, TimeSheetReview } from "../typings"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { useAppNotificationStore } from "stores"
import { formatDateReadable, formatTimeReadble } from "utils/formatDate"
import calenderDots from "assets/svg/CalendarDots.svg"
import tabletIcon from "assets/svg/tablet.svg"
import lecternIcon from "assets/svg/Lectern.svg"
import globeIcon from "assets/svg/globe.svg"
import dayjs from "dayjs"
import {
  FilterOption,
  FilterRadio,
  FilterValueAttendantRecord,
} from "./AttendantRecordToolBar"
import { formatTime } from "pages/employee-dashboard/time-and-attendance/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean
  onClose: () => void
  timesheet?: ITimeSheetDocsEntity
  onApprove?: (id: number) => void
  onReject?: (id: number) => void
  isApproving?: boolean
  isRejecting?: boolean
  timesheetReviewDetails: TimeSheetReview | undefined
}

// ─── Helpers ──────────────────────────────────────────────────────────────────s

const methodLabel = (method?: string): string => {
  if (method === "mobile-app") return "Mobile App"
  if (method === "hardware-device") return "Hardware Device"
  if (method === "web-app") return "Web App"
  return method ?? "—"
}

const methodIcon = (method?: string): string => {
  if (method === "mobile-app") return tabletIcon
  if (method === "hardware-device") return lecternIcon
  return globeIcon
}

const planTypeLabel = (type?: string): string => {
  if (type === "rostered") return "Rostered plan"
  if (type === "flexible") return "Any-time plan"
  return ""
}

const buildClockInFlags = (e?: ITimeSheetDocsEntity): string[] => {
  if (!e) return []
  const flags: string[] = []
  if (e.origin && String(e.origin).toLowerCase() === "manual") {
    flags.push("Manual entry")
  }
  if (
    e.geofenceStatus &&
    !["WITHIN", "INSIDE"].includes(String(e.geofenceStatus).toUpperCase())
  ) {
    flags.push("Outside geofence")
  }
  if (e.lateClockin) flags.push("Late arrival")
  if (e.earlyClockin) flags.push("Early arrival")
  return flags
}

const buildClockOutFlags = (e?: ITimeSheetDocsEntity): string[] => {
  if (!e) return []
  const flags: string[] = []
  if (e.origin && String(e.origin).toLowerCase() === "manual") {
    flags.push("Manual entry")
  }
  if (
    e.geofenceStatus &&
    !["WITHIN", "INSIDE"].includes(String(e.geofenceStatus).toUpperCase())
  ) {
    flags.push("Outside geofence")
  }
  if (e.earlyClockout) flags.push("Early departure")
  if (e.lateClockout) flags.push("Late departure")
  if (e.overtime) flags.push("Overtime")
  return flags
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Row: FC<{ label: string; children: ReactNode; tall?: boolean }> = ({
  label,
  children,
  tall,
}) => (
  <div
    className={`flex border-b border-slate-200 ${
      tall ? "items-start" : "items-center"
    }`}
  >
    <div className="w-[140px] shrink-0 bg-slate-50 self-stretch px-4 py-3 text-sm text-slate-500">
      {label}
    </div>
    <div className="flex-1 px-4 py-3 text-sm text-slate-900">{children}</div>
  </div>
)

const FlagPill: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="border border-slate-200 rounded-full px-3 py-1 text-xs text-slate-900 whitespace-nowrap">
    {children}
  </span>
)

// ─── Component ────────────────────────────────────────────────────────────────

const TimesheetDetailDrawer: FC<Props> = ({
  open,
  onClose,
  timesheet,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
  timesheetReviewDetails,
}) => {
  const { toast } = useAppNotificationStore()
  const [canApproveReject] = usePermissions({
    allowedPermissions: [
      TimeAndAttendancePermission.CanApproveAndRejectAttendanceEntries,
    ],
  })
  const [selectedFilter, setSelectedFilter] = useState("all")
  const shiftPlan = timesheet?.shiftPlan
  const shiftPeriod = timesheet?.shiftPeriod

  const clockInFlags = buildClockInFlags(timesheet)
  const clockOutFlags = buildClockOutFlags(timesheet)

  const headerDate = timesheet?.clockIn
    ? formatDateReadable(timesheet.clockIn)
    : ""
  const employeeName = timesheet?.employee?.fullName ?? ""

  const showActions = timesheet?.approvalStatus === "pending"

  const filterOptions: FilterOption[] = [
    { value: "all", label: "All events" },
    // {
    //   value: "edit",
    //   label: "Edit event",
    // },
  ]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={600}
      closable={false}
      headerStyle={{ display: "none" }}
      bodyStyle={{ padding: 0 }}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <p className="text-sm font-bold m-0">
          Timesheet for {headerDate}
          {employeeName ? ` | ${employeeName}` : ""}
        </p>
      </div>
      {/* Tabs */}
      <Tabs
        defaultActiveKey="1"
        className="my-5 [&_.tab-text]:text-slate-500 [&_.ant-tabs-tab-active_.tab-text]:!text-slate-900 [&_.ant-tabs-ink-bar]:!bg-slate-900"
        items={[
          {
            key: "1",
            label: <p className="px-4">Details</p>,
            children: (
              <>
                {/* Rows */}
                <Row label="Name">{employeeName || "—"}</Row>
                <Row label="Clock-in">{formatTime(timesheet?.clockIn)}</Row>
                <Row label="Clock-out">{formatTime(timesheet?.clockOut)}</Row>
                <Row label="Method">
                  <span className="inline-flex items-center gap-2">
                    <img
                      src={methodIcon(timesheet?.method)}
                      width={15}
                      height={15}
                      alt=""
                    />
                    {methodLabel(timesheet?.method)}
                  </span>
                </Row>

                <Row
                  label="Clock-in flags"
                  tall={clockInFlags.length > 0}
                >
                  {clockInFlags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {clockInFlags.map((f) => (
                        <FlagPill key={f}>{f}</FlagPill>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </Row>

                <Row
                  label="Clock-out flags"
                  tall={clockOutFlags.length > 0}
                >
                  {clockOutFlags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {clockOutFlags.map((f) => (
                        <FlagPill key={f}>{f}</FlagPill>
                      ))}
                    </div>
                  ) : (
                    "—"
                  )}
                </Row>

                <Row
                  label="Shift plan"
                  tall
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-bold">{shiftPlan?.title ?? "—"}</span>
                    {shiftPlan?.type && (
                      <span className="inline-flex items-center gap-1 text-slate-500 text-xs">
                        <img
                          src={calenderDots}
                          width={14}
                          height={14}
                          alt=""
                        />
                        {planTypeLabel(shiftPlan.type)}
                      </span>
                    )}
                  </div>
                </Row>

                <Row label="Shift period">{shiftPeriod?.title ?? "—"}</Row>

                <Row
                  label="Reason"
                  tall={!!timesheet?.reason}
                >
                  {timesheet?.reason ?? "—"}
                </Row>

                <Row label="Actions">
                  {showActions ? (
                    <div className="flex items-center gap-2">
                      <Button
                        type="default"
                        className="w-10 h-10 p-0 flex items-center justify-center rounded-md border border-slate-300"
                        loading={isApproving}
                        onClick={() => {
                          if (!canApproveReject) {
                            toast.unauthorized()
                            return
                          }
                          timesheet && onApprove?.(timesheet.id)
                        }}
                        aria-label="Approve"
                      >
                        <CheckIcon className="w-4 h-4 text-slate-900" />
                      </Button>
                      <Button
                        type="default"
                        className="w-10 h-10 p-0 flex items-center justify-center rounded-md border border-slate-300"
                        loading={isRejecting}
                        onClick={() => {
                          if (!canApproveReject) {
                            toast.unauthorized()
                            return
                          }
                          timesheet && onReject?.(timesheet.id)
                        }}
                        aria-label="Reject"
                      >
                        <XMarkIcon className="w-4 h-4 text-slate-900" />
                      </Button>
                    </div>
                  ) : (
                    <span className="text-slate-500 capitalize">
                      {timesheet?.approvalStatus ?? "—"}
                    </span>
                  )}
                </Row>
              </>
            ),
          },
          {
            key: "2",
            label: <p>Activity timeline</p>,
            children: (
              <div className="px-4">
                {/* Filter Radio Group */}
                <Radio.Group
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="flex items-center gap-1 flex-wrap"
                >
                  {filterOptions.map(({ value, label, count }) => (
                    <FilterRadio
                      key={value}
                      value={value}
                      label={label}
                      selectedFilter={
                        selectedFilter as FilterValueAttendantRecord
                      }
                      count={count}
                    />
                  ))}
                </Radio.Group>
                {/* 1. Wrap the entire layout inside your tracking line container */}
                <TimelineContainer padding="pt-5">
                  <div className="flex flex-col gap-5 mt-4 w-full">
                    {/* Step 1: Clock-In */}
                    <TimelineStep>
                      <ActivityTimelineWrapper
                        time={formatTimeWithAmPm(timesheet?.clockIn)}
                        type="Clock-in"
                        date={formatDateReadable(
                          timesheet?.clockIn || dayjs().toISOString()
                        )}
                        device={methodLabel(timesheet?.method)}
                      />
                    </TimelineStep>

                    {/* Step 2: Clock-Out */}
                    <TimelineStep>
                      <ActivityTimelineWrapper
                        time={formatTimeWithAmPm(timesheet?.clockOut)}
                        type="Clock-out"
                        date={formatDateReadable(
                          timesheet?.clockOut || dayjs().toISOString()
                        )}
                        device={methodLabel(timesheet?.method)}
                      />
                    </TimelineStep>

                    {/* Step 3: Manual Timesheet Edit block */}
                    {/* <TimelineStep>
                      <div>
                        <div className="flex items-center gap-2 font-bold text-sm text-slate-400 mb-1">
                          <span>0:00 am</span>
                          <span className="bg-slate-200 w-1.5 h-1.5 rounded-full"></span>
                          <span>Jan 00 2022</span>
                        </div>
                        <div>
                          <p className="font-bold text-slate-900 leading-tight">
                            Timesheet edit
                          </p>
                          <p className="mt-2 text-[15px] font-medium text-slate-500 tracking-wide">
                            Changed clock-in from 00:00pm to 00:00 am
                          </p>
                        </div>
                      </div>
                    </TimelineStep> */}
                  </div>
                </TimelineContainer>
              </div>
            ),
          },
          {
            key: "3",
            label: <p>Approval timeline</p>,
            children: (
              <TimelineContainer>
                {timesheetReviewDetails?.data?.length ? (
                  timesheetReviewDetails?.data
                    ?.slice()
                    ?.sort(
                      (a, b) =>
                        new Date(a.updatedDate).getTime() -
                        new Date(b.updatedDate).getTime()
                    )
                    ?.map((item, index) => (
                      <TimelineStep key={item.id}>
                        {/* Your custom structured step UI content goes directly here */}
                        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                          <span>{formatTimeWithAmPm(item.updatedDate)}</span>
                          <span className="w-1.5 h-1.5 bg-slate-200 rounded-full"></span>
                          <span>{formatDateReadable(item.updatedDate)}</span>
                        </div>

                        <div className="flex items-center gap-2 font-bold text-slate-900 leading-tight">
                          <span>
                            Approver {index + 1} [{item.updatedByName}]
                          </span>
                          <span className="text-slate-300 font-light text-xl">
                            /
                          </span>
                          {item.reviewActionId === 1 ? (
                            <span className="text-[#00A63E]">Approved</span>
                          ) : item.reviewActionId === 2 ? (
                            <span className="text-[#E7000B]">Rejected</span>
                          ) : (
                            <span className="text-slate-500">Pending</span>
                          )}
                        </div>
                      </TimelineStep>
                    ))
                ) : (
                  <p className="font-bold flex items-center justify-center">
                    No Approval Timeline
                  </p>
                )}
              </TimelineContainer>
            ),
          },
        ]}
      />
    </Drawer>
  )
}

export default TimesheetDetailDrawer

const ActivityTimelineWrapper = ({
  time,
  type,
  date,
  device,
}: {
  time: string
  type: string
  date: string
  device: string
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-slate-500">{time}</span>
        <span className="bg-slate-200 w-1 h-1 rounded-full"></span>
        <span className="text-slate-500">{date}</span>
      </div>
      <div className="flex items-end gap-2">
        <p className="font-bold">{type}</p>
        <span className="text-slate-300">/</span>
        <p className="font-bold">{device}</p>
      </div>
    </div>
  )
}

export const formatTimeWithAmPm = (
  dateString: string | undefined | null
): string => {
  if (!dateString) return ""
  return dayjs(dateString).format("h:mm a") // Use 'hh' instead of 'h' if you want a leading zero (e.g., "01:50 am")
}

interface TimelineContainerProps {
  padding?: string
  children: ReactNode
}

function TimelineContainer({ children, padding }: TimelineContainerProps) {
  return (
    <div className={`max-w-xl ${padding ?? "px-6"} bg-white`}>
      {/* Creates the shared vertical baseline track */}
      <div className="relative border-l-2 border-slate-100 pl-6 ml-2 flex flex-col gap-5">
        {children}
      </div>
    </div>
  )
}

interface TimelineStepProps {
  children: ReactNode
}

function TimelineStep({ children }: TimelineStepProps) {
  return (
    <div className="relative group">
      {/* Absolute positioned timeline anchor circle node */}
      <div className="absolute -left-[28px] top-[34px] w-[6px] h-[6px] bg-slate-200 border-2 border-slate-200 rounded-full z-10"></div>

      {/* Target step wrapper injection container */}
      {children}
    </div>
  )
}
